import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, Globe } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { Label } from '../../../components/ui/label';
import {
  type LocationRegion,
  type RegionOption,
  getAllRegions,
  getRegionByCode,
  getRegionName,
} from './MultiRegionPicker';

// Re-export types and helpers from MultiRegionPicker for backwards compatibility
export { type LocationRegion, type RegionOption, getRegionByCode, getRegionName, getAllRegions };

// Group regions by type for dropdown organization
const regionOptions = getAllRegions();
const regionsByType = {
  business: regionOptions.filter((r) => r.type === 'business'),
  un_macro: regionOptions.filter((r) => r.type === 'un_macro'),
  un_sub: regionOptions.filter((r) => r.type === 'un_sub'),
  un_intermediate: regionOptions.filter((r) => r.type === 'un_intermediate'),
  special: regionOptions.filter((r) => r.type === 'special'),
};

interface RegionPickerProps {
  value: LocationRegion | null;
  onChange: (region: LocationRegion | null) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default';
  className?: string;
  /** Whether to show the GLOBAL option (default: false) */
  showGlobal?: boolean;
}

export function RegionPicker({
  value,
  onChange,
  label,
  placeholder,
  helperText,
  error,
  errorMessage,
  disabled,
  required,
  fullWidth = true,
  size = 'default',
  className,
  showGlobal = false,
}: RegionPickerProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);

  const selectedRegion = useMemo(
    () => (value ? regionOptions.find((r) => r.code === value) : null),
    [value]
  );

  const handleSelect = (code: string) => {
    if (code === value) {
      onChange(null);
    } else {
      onChange(code as LocationRegion);
    }
    setOpen(false);
  };

  // Filter grouped regions based on showGlobal
  const businessRegions = regionsByType.business;
  const macroRegions = regionsByType.un_macro;
  const subRegions = regionsByType.un_sub;
  const intermediateRegions = regionsByType.un_intermediate;

  const renderRegionItem = (region: RegionOption) => (
    <CommandItem
      key={region.code}
      value={`${region.name} ${region.description}`}
      onSelect={() => handleSelect(region.code)}
    >
      <Check
        className={cn(
          'me-2 h-4 w-4',
          value === region.code ? 'opacity-100' : 'opacity-0'
        )}
      />
      <Globe className="me-2 h-4 w-4 text-muted-foreground" />
      <div className="flex-1">
        <p className="text-sm">{region.name}</p>
        <p className="text-xs text-muted-foreground">
          {region.description}
          {region.countryCount > 0 && ` (${region.countryCount} countries)`}
        </p>
      </div>
    </CommandItem>
  );

  return (
    <div className={cn(fullWidth && 'w-full', className)}>
      {label && (
        <div className="mb-1.5 leading-none">
          <Label required={required}>
            {label}
          </Label>
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'justify-between font-normal',
              fullWidth && 'w-full',
              size === 'sm' && 'h-8 text-sm',
              !selectedRegion && 'text-muted-foreground',
              error && 'border-destructive'
            )}
          >
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground" />
              {selectedRegion ? (
                <span>{selectedRegion.name}</span>
              ) : (
                <span>{placeholder || t('pickers.selectRegion')}</span>
              )}
            </div>
            <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command>
            <CommandInput placeholder={t('pickers.searchRegion')} />
            <CommandList>
              <CommandEmpty>{t('pickers.noRegionFound')}</CommandEmpty>

              {/* Business Regions - Most commonly used */}
              {businessRegions.length > 0 && (
                <CommandGroup heading={t('pickers.regionGroups.business', 'Business Regions')}>
                  {businessRegions.map(renderRegionItem)}
                </CommandGroup>
              )}

              {/* UN M49 Macro Regions (Continents) */}
              {macroRegions.length > 0 && (
                <CommandGroup heading={t('pickers.regionGroups.continents', 'Continents')}>
                  {macroRegions.map(renderRegionItem)}
                </CommandGroup>
              )}

              {/* UN M49 Sub-Regions */}
              {subRegions.length > 0 && (
                <CommandGroup heading={t('pickers.regionGroups.subRegions', 'Sub-Regions')}>
                  {subRegions.map(renderRegionItem)}
                </CommandGroup>
              )}

              {/* UN M49 Intermediate Regions */}
              {intermediateRegions.length > 0 && (
                <CommandGroup heading={t('pickers.regionGroups.intermediateRegions', 'Intermediate Regions')}>
                  {intermediateRegions.map(renderRegionItem)}
                </CommandGroup>
              )}

              {/* Global option */}
              {showGlobal && regionsByType.special.length > 0 && (
                <CommandGroup heading={t('pickers.regionGroups.special', 'Special')}>
                  {regionsByType.special.map(renderRegionItem)}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {(errorMessage || helperText) && (
        <p className={cn(
          'text-xs mt-1',
          error || errorMessage ? 'text-destructive' : 'text-muted-foreground'
        )}>
          {errorMessage || helperText}
        </p>
      )}
    </div>
  );
}

export type { RegionPickerProps };
